import { type ReactNode, useEffect, useState } from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Radio,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

export interface ReviewIdentityProspect {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  dob?: string | null;
  reason?: string | null;
}

export interface ReviewIdentityMatch {
  patientId: string;
  name?: string | null;
  dob?: string | null;
  email?: string | null;
  phone?: string | null;
  /** Match confidence, 0-100. */
  confidence: number;
}

/** Exactly one of the two shapes — mirrors the API's XOR contract. */
export type ReviewIdentitySelection =
  | { patientId: string }
  | { createNew: true };

export interface ReviewIdentityDialogViewProps {
  open: boolean;
  onClose: () => void;
  prospect: ReviewIdentityProspect | null;
  isLoading?: boolean;
  /** True when the prospect/matches could not be loaded (e.g. the booking
   * was resolved by someone else while this queue was open). */
  loadFailed?: boolean;
  matches: ReviewIdentityMatch[];
  isSaving?: boolean;
  onSubmit: (selection: ReviewIdentitySelection) => void;
  /** Subject-language singular, e.g. "patient" / "client". */
  subjectSingular: string;
}

const CREATE_NEW = 'create-new';

function confidenceColor(confidence: number): 'success' | 'warning' | 'default' {
  if (confidence >= 80) return 'success';
  if (confidence >= 50) return 'warning';
  return 'default';
}

type FieldComparison = 'match' | 'differ' | 'unknown';

const normalizeName = (value: string) =>
  value.toLowerCase().replace(/\s+/g, ' ').trim();
const normalizeText = (value: string) => value.toLowerCase().trim();
const normalizePhone = (value: string) => value.replace(/\D/g, '').slice(-10);

function compareField(
  entered: string | null | undefined,
  onFile: string | null | undefined,
  normalize: (value: string) => string,
): FieldComparison {
  if (!entered || !onFile) return 'unknown';
  return normalize(entered) === normalize(onFile) ? 'match' : 'differ';
}

interface ComparisonRow {
  label: string;
  onFile: string | null | undefined;
  comparison: FieldComparison;
}

/** Field-by-field comparison of what the booker entered vs the candidate's
 * chart — the reviewable evidence behind the confidence score. */
function buildComparisonRows(
  prospect: ReviewIdentityProspect | null,
  match: ReviewIdentityMatch,
): ComparisonRow[] {
  const enteredName = [prospect?.firstName, prospect?.lastName]
    .filter(Boolean)
    .join(' ');
  return [
    {
      label: 'Name',
      onFile: match.name,
      comparison: compareField(enteredName, match.name, normalizeName),
    },
    {
      label: 'Email',
      onFile: match.email,
      comparison: compareField(prospect?.email, match.email, normalizeText),
    },
    {
      label: 'Phone',
      onFile: match.phone,
      comparison: compareField(prospect?.phone, match.phone, normalizePhone),
    },
    {
      label: 'DOB',
      onFile: match.dob,
      comparison: compareField(prospect?.dob, match.dob, normalizeText),
    },
  ];
}

/** Identity review for a self-booked prospect (ADR-0077 promotion step).
 *
 * Staff either link the booking to a ranked existing-{patient} match or
 * create a new chart from the booking's contact details. The choice is
 * always explicit — nothing is preselected (ADR-0076: never auto-merge).
 */
export function ReviewIdentityDialogView({
  open,
  onClose,
  prospect,
  isLoading = false,
  loadFailed = false,
  matches,
  isSaving = false,
  onSubmit,
  subjectSingular,
}: ReviewIdentityDialogViewProps) {
  const [selection, setSelection] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelection(null);
  }, [open]);

  const prospectName =
    [prospect?.firstName, prospect?.lastName].filter(Boolean).join(' ') ||
    'Unknown requester';
  const contactLine = [prospect?.email, prospect?.phone]
    .filter(Boolean)
    .join(' · ');

  const handleSubmit = () => {
    if (!selection) return;
    onSubmit(
      selection === CREATE_NEW ? { createNew: true } : { patientId: selection },
    );
  };

  const optionCard = (params: {
    value: string;
    title: string;
    subtitle?: string;
    ariaLabel: string;
    trailing?: ReactNode;
    leading?: ReactNode;
    content?: ReactNode;
  }) => {
    const selected = selection === params.value;
    return (
      <Paper
        key={params.value}
        variant="outlined"
        onClick={() => {
          if (!isSaving) setSelection(params.value);
        }}
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          cursor: isSaving ? 'default' : 'pointer',
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.selected' : 'background.paper',
        }}
      >
        <Radio
          checked={selected}
          disabled={isSaving}
          slotProps={{ input: { 'aria-label': params.ariaLabel } }}
          sx={{ p: 0.5 }}
        />
        {params.leading}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {params.title}
          </Typography>
          {params.subtitle && (
            <Typography variant="caption" color="text.secondary">
              {params.subtitle}
            </Typography>
          )}
          {params.content}
        </Box>
        {params.trailing}
      </Paper>
    );
  };

  const comparisonIcon = (comparison: FieldComparison) => {
    if (comparison === 'match') {
      return <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} color="success" />;
    }
    if (comparison === 'differ') {
      return <HighlightOffOutlinedIcon sx={{ fontSize: 14 }} color="warning" />;
    }
    return <RemoveOutlinedIcon sx={{ fontSize: 14 }} color="disabled" />;
  };

  const comparisonRows = (match: ReviewIdentityMatch) => (
    <Stack spacing={0.25} sx={{ mt: 0.75 }}>
      {buildComparisonRows(prospect, match).map(row => (
        <Stack
          key={row.label}
          direction="row"
          spacing={0.75}
          sx={{ alignItems: 'center' }}
        >
          {comparisonIcon(row.comparison)}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: 42, flexShrink: 0 }}
          >
            {row.label}
          </Typography>
          <Typography
            variant="caption"
            color={row.comparison === 'differ' ? 'warning.main' : 'text.primary'}
            sx={{ wordBreak: 'break-word' }}
          >
            {row.onFile || 'not on file'}
            {row.comparison === 'differ' && ' — differs from what was entered'}
            {row.comparison === 'unknown' && row.onFile ? ' — nothing entered to compare' : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Review identity
        <Typography variant="body2" color="text.secondary">
          Link this booking to an existing {subjectSingular} or start a new
          chart. The calendar slot is not affected.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loadFailed ? (
          <Alert severity="warning">
            This booking request is no longer available — it may have been
            resolved by someone else. Close and refresh the queue.
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={64} />
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {prospectName}
              </Typography>
              {contactLine && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {contactLine}
                </Typography>
              )}
              {prospect?.dob && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  DOB {prospect.dob}
                </Typography>
              )}
              {prospect?.reason && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontStyle: 'italic' }}
                >
                  “{prospect.reason}”
                </Typography>
              )}
            </Box>

            <Divider />

            <Box role="radiogroup" aria-label="Identity resolution">
              <Stack spacing={1}>
                {matches.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No likely matches in your existing {subjectSingular} list.
                  </Typography>
                ) : (
                  matches.map(match =>
                    optionCard({
                      value: match.patientId,
                      title: match.name || 'Unnamed record',
                      ariaLabel: `Link to ${match.name || 'unnamed record'}`,
                      content: comparisonRows(match),
                      trailing: (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={confidenceColor(match.confidence)}
                          label={`${match.confidence}% match`}
                        />
                      ),
                    }),
                  )
                )}
                {optionCard({
                  value: CREATE_NEW,
                  title: `New ${subjectSingular}`,
                  subtitle: `Start a chart from the booking's contact details`,
                  ariaLabel: `Create a new ${subjectSingular}`,
                  leading: <PersonAddOutlinedIcon color="action" />,
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selection || isSaving || isLoading || loadFailed}
          startIcon={
            isSaving ? <CircularProgress size={14} color="inherit" /> : undefined
          }
        >
          {selection === CREATE_NEW
            ? `Create ${subjectSingular}`
            : `Link ${subjectSingular}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
